// client/src/lib/organizationLoad.ts
//
// Loading and selecting the signed-in user's organization.
//
// WHY THIS IS ITS OWN MODULE
//
// `currentOrganization` is a hard prerequisite for a large share of the app:
// every org-scoped query is gated on it (`enabled: !!email && !!orgId` in
// useMyScheduleBlocks, and the same shape elsewhere). A gated query does not
// fail — it never runs — so a screen whose data never arrived renders exactly
// like a screen whose data is genuinely empty.
//
// That is not hypothetical. A technician's PWA cold-started without signal,
// the single membership fetch threw, `currentOrganization` stayed null, and
// TechToday showed "No jobs scheduled for today" for days while he was in fact
// dispatched. The provider fetched once in a `useEffect` with no retry and no
// recovery trigger, and an installed PWA that is backgrounded rather than
// force-closed keeps that same JS context — so one bad startup persisted until
// he swiped the app away.
//
// The load therefore retries, and the pieces that decide WHICH org is current
// live here as pure functions so the dead-zone path can be tested without
// rendering a provider.
import { collectionGroup, doc, getDoc, query, where } from 'firebase/firestore';
import { getDocsEarned } from './firestore/earnedRead';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { toMembership } from './organizationMembership';
import type { Organization, OrganizationMember } from '../types/firestore';

export interface OrganizationLoadResult {
  userMemberships: OrganizationMember[];
  orgs: Organization[];
}

/** The org + membership pair a load resolved to. Both null when the user has none. */
export interface OrganizationSelection {
  organization: Organization | null;
  membership: OrganizationMember | null;
  /**
   * True when the user's preferred `organizationId` was not among their active
   * memberships and a fallback was chosen instead. The caller logs it; it is
   * not an error state.
   */
  fellBackFromPreferred: boolean;
}

/**
 * Fetch the user's active memberships and the active organizations behind them.
 * Exported for the retry wrapper and for tests; callers should prefer
 * `loadUserOrganizations`.
 */
export async function fetchUserMembershipsAndOrgs(
  userId: string
): Promise<OrganizationLoadResult> {
  const membershipsQuery = query(
    collectionGroup(db, 'members'),
    where('userId', '==', userId),
    where('status', '==', 'active')
  );

  // EARNED read: with no signal and a memory cache, getDocs resolves EMPTY from
  // cache instead of throwing — which read as "no memberships", so the caller
  // showed "No workspace assigned" with no error set and never retried, leaving
  // the technician stuck until a force-close. getDocsEarned refuses a
  // cache-served empty (DegradedReadError, retriable), and still serves stale
  // cached memberships when there are some.
  const membershipsSnapshot = await getDocsEarned(membershipsQuery, 'your team membership');
  const userMemberships = membershipsSnapshot.docs
    .map(toMembership)
    .filter((m): m is OrganizationMember => m !== null);

  const orgPromises = userMemberships.map(async (membership) => {
    const orgDoc = await getDoc(doc(db, COLLECTIONS.ORGANIZATIONS, membership.organizationId));
    return orgDoc.exists() ? (orgDoc.data() as Organization) : null;
  });

  const orgs = (await Promise.all(orgPromises)).filter(
    (org): org is Organization => org !== null && org.isActive
  );

  return { userMemberships, orgs };
}

/**
 * Decide which organization is current.
 *
 * Pure so the selection rules are testable on their own — this is the step
 * `refreshOrganizations` used to skip entirely, which meant a manual refresh
 * repopulated the org LIST while leaving `currentOrganization` null. A retry
 * that cannot restore the thing that broke is not a retry.
 *
 * `orgs` is filtered by exists/isActive while `userMemberships` is not, so the
 * two arrays are NOT index-aligned — pairing [0] with [0] can mix orgs. The
 * membership is always looked up by the chosen org's id.
 */
export function selectOrganization(
  orgs: readonly Organization[],
  userMemberships: readonly OrganizationMember[],
  preferredOrgId?: string
): OrganizationSelection {
  const membershipFor = (org: Organization) =>
    userMemberships.find((m) => m.organizationId === org.organizationId) ?? null;

  if (preferredOrgId) {
    const preferredOrg = orgs.find((org) => org.organizationId === preferredOrgId);
    const preferredMembership = userMemberships.find(
      (m) => m.organizationId === preferredOrgId
    );
    if (preferredOrg && preferredMembership) {
      return {
        organization: preferredOrg,
        membership: preferredMembership,
        fellBackFromPreferred: false,
      };
    }
  }

  const fallbackOrg = orgs[0];
  if (!fallbackOrg) {
    return { organization: null, membership: null, fellBackFromPreferred: false };
  }

  return {
    organization: fallbackOrg,
    membership: membershipFor(fallbackOrg),
    // Only a FALLBACK if a preference was expressed and could not be honoured.
    fellBackFromPreferred: Boolean(preferredOrgId),
  };
}

/** Attempt delays in ms. Three retries, then the caller surfaces the failure. */
export const RETRY_DELAYS_MS = [500, 2_000, 5_000] as const;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Firestore codes that mean "the answer will not change if you ask again".
 *
 * Retrying these is worse than useless: the caller re-arms reconnect and
 * foreground retries while an error is set, so a genuinely denied account
 * would re-run the whole backoff sequence every time the tech opens the app —
 * load on Firestore, and an authorization problem dressed up as bad signal.
 * A denial should surface promptly and stay surfaced.
 */
const TERMINAL_ERROR_CODES = new Set([
  'permission-denied',
  'unauthenticated',
  'invalid-argument',
  'failed-precondition',
  'not-found',
]);

/** True when another attempt could plausibly succeed. */
export function isRetriableError(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  // A network failure carries no Firestore code at all — that is the dead-zone
  // case this retry exists for, so an unknown shape is treated as retriable.
  if (typeof code !== 'string') return true;
  return !TERMINAL_ERROR_CODES.has(code);
}

/**
 * Load with bounded retries.
 *
 * A technician cold-starting in a dead zone is the common case, not the edge
 * case, so a single transient failure must not decide the whole session. The
 * delays are short and finite: this runs before the app can show anything
 * org-scoped, so it must settle quickly either way, and the caller pairs it
 * with reconnect/foreground recovery for failures that outlast the retries.
 *
 * `deps` is injectable so tests can drive the failure path without timers or
 * network.
 */
export async function loadUserOrganizations(
  userId: string,
  deps: {
    fetch?: (userId: string) => Promise<OrganizationLoadResult>;
    delay?: (ms: number) => Promise<void>;
    onAttemptFailed?: (error: unknown, attempt: number) => void;
  } = {}
): Promise<OrganizationLoadResult> {
  const fetchFn = deps.fetch ?? fetchUserMembershipsAndOrgs;
  const delayFn = deps.delay ?? wait;

  let lastError: unknown;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      return await fetchFn(userId);
    } catch (err) {
      lastError = err;
      deps.onAttemptFailed?.(err, attempt);
      // A denial is an answer, not a blip — surface it now.
      if (!isRetriableError(err)) break;
      const nextDelay = RETRY_DELAYS_MS[attempt];
      if (nextDelay === undefined) break;
      await delayFn(nextDelay);
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error('Failed to load organizations');
}
